/**
 * vm-motion.js: Varmth shared motion + UI runtime (foundation file).
 * ES module, no dependencies, loaded once from snippets/vm-head.liquid.
 * Spec: site/DESIGN-SPEC.md §4.0. Usage: site/theme/CONVENTIONS.md §6.
 *
 * One singleton lives on window.vmMotion, so a second copy of this module
 * (loaded from another URL) shares the same loop and registry.
 *
 * Data-attribute API (no section JS needed):
 *   [data-vm-scrub="pin|pass|enter"]   writes --vm-p (0..1, lerped) while on screen.
 *       data-vm-lerp="0.18"            smoothing per 60 fps frame (touch default 0.3)
 *       data-vm-steps="3"              also writes data-vm-step="0..2", fires vm:step
 *       child [data-vm-stage]          pin mode measures the sticky stage, not the viewport
 *   [data-vm-inview="0.5"]             sets data-vm-seen once when 50% visible, fires vm:inview
 *   [data-vm-autoplay]                 container of things that move on their own:
 *       data-vm-paused                 set while paused (user, reduced motion, Save-Data)
 *       data-vm-offscreen              set while under 10% visible
 *       data-vm-hover-pause            CSS pauses animations on hover / focus-within
 *       button[data-vm-toggle]         pause / play toggle (aria-pressed="true" = paused)
 *       video[data-vm-video]           lazy-loaded, plays only while >= 50% visible
 *         data-vm-defer                  wait for window load first (first-viewport loops)
 *
 * JS API (window.vmMotion or `import { track } from` this file):
 *   track(el, onProgress?, { mode, lerp, steps }) -> untrack()
 *   inView(el, onEnter, { threshold, once }) -> stop()
 *   lazyVideo(videoEl), reduced(), saveData(), onReducedChange(fn) -> off()
 *   ramp(p, start, end), clamp(v, min, max), scan(root)
 *
 * Horizon note: at >= 990px Horizon scrolls .page-wrapper, not the window, so
 * nothing here reads window.scrollY. Progress comes from element rects, read
 * once per frame for on-screen elements only, then written in one pass.
 */

const root = document.documentElement;

/** @type {any} */
const VM = window.vmMotion || (window.vmMotion = createVM());

export default VM;
export const track = VM.track;
export const untrack = VM.untrack;
export const inView = VM.inView;
export const lazyVideo = VM.lazyVideo;
export const reduced = VM.reduced;
export const saveData = VM.saveData;
export const onReducedChange = VM.onReducedChange;
export const ramp = VM.ramp;
export const clamp = VM.clamp;
export const scan = VM.scan;

function createVM() {
  const mqReduce = matchMedia('(prefers-reduced-motion: reduce)');
  const coarse = matchMedia('(pointer: coarse)').matches;
  const conn = /** @type {any} */ (navigator).connection;
  const state = { reduced: mqReduce.matches, saveData: !!(conn && conn.saveData) };
  const reducedFns = new Set();

  const clampFn = (v, min = 0, max = 1) => (v < min ? min : v > max ? max : v);
  const rampFn = (p, start, end) => clampFn((p - start) / (end - start || 1));
  const num = (v, d) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : d;
  };

  root.classList.add('vm-js');
  root.classList.toggle('vm-rm', state.reduced);
  if (state.saveData) root.setAttribute('data-vm-save-data', '');

  /* ------------------------------------------------------------------ */
  /* Scroll progress                                                     */
  /* ------------------------------------------------------------------ */

  /** @type {Map<Element, any>} */
  const items = new Map();
  const live = new Set();
  let raf = 0;
  let last = 0;
  let listening = false;
  let slow = 0;
  let frames = 0;

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const it = items.get(entry.target);
        if (!it) continue;
        it.visible = entry.isIntersecting;
        entry.target.toggleAttribute('data-vm-active', it.visible);
        if (it.visible) live.add(it);
      }
      listen(live.size > 0);
      kick();
    },
    { rootMargin: '25% 0px' }
  );

  const onScroll = () => kick();
  function listen(on) {
    if (on === listening) return;
    listening = on;
    // Scroll events don't bubble, but the capture phase sees them on any
    // scroller (window or Horizon's .page-wrapper). The handler only wakes the loop.
    const method = on ? 'addEventListener' : 'removeEventListener';
    document[method]('scroll', onScroll, { capture: true, passive: true });
    window[method]('resize', onResize, { passive: true });
  }

  function onResize() {
    for (const it of items.values()) it.stageTop = null;
    kick();
  }

  function kick() {
    if (!raf && live.size) raf = requestAnimationFrame(frame);
  }

  function progress(it, rect, vh) {
    if (it.mode === 'pass') return clampFn((vh - rect.top) / (vh + rect.height));
    if (it.mode === 'enter') return clampFn((vh - rect.top) / vh);
    // pin: 0 when the stage starts sticking, 1 when it releases.
    const stage = it.stage;
    let top = 0;
    let span = rect.height - vh;
    if (stage && stage.isConnected) {
      if (it.stageTop == null) it.stageTop = num(getComputedStyle(stage).top, 0);
      top = it.stageTop;
      span = rect.height - stage.offsetHeight;
    }
    return span > 0 ? clampFn((top - rect.top) / span) : rect.top <= top ? 1 : 0;
  }

  function frame(t) {
    raf = 0;
    const dt = last ? Math.min(t - last, 100) : 16.7;
    last = t;
    const vh = root.clientHeight || innerHeight;
    let again = false;

    // Read phase: every measurement first, so the write phase can't force layout.
    const work = [];
    for (const it of live) {
      if (!it.el.isConnected) {
        drop(it.el);
        continue;
      }
      work.push([it, progress(it, it.el.getBoundingClientRect(), vh)]);
    }

    // Write phase.
    for (const [it, target] of work) {
      const k = state.reduced || it.lerp >= 1 ? 1 : 1 - Math.pow(1 - it.lerp, dt / 16.667);
      let p = it.p + (target - it.p) * k;
      if (Math.abs(target - p) < 0.0005) p = target;
      it.p = p;
      if (p !== it.written) write(it);
      if (p !== target) again = true;
      else if (!it.visible) live.delete(it);
    }

    // Lite mode: sustained frames slower than ~55 fps while scrubbing.
    if (again && !document.hidden) {
      frames++;
      if (dt > 18.2) slow++;
      if (frames >= 90) {
        if (slow / frames > 0.6 && !root.hasAttribute('data-vm-lite')) {
          root.setAttribute('data-vm-lite', '');
          document.dispatchEvent(new CustomEvent('vm:lite'));
        }
        frames = slow = 0;
      }
    }

    if (again) raf = requestAnimationFrame(frame);
    else last = 0;
  }

  function write(it) {
    it.written = it.p;
    it.el.style.setProperty('--vm-p', it.p.toFixed(4));
    if (it.steps > 1) {
      const step = Math.min(it.steps - 1, Math.floor(it.p * it.steps));
      if (step !== it.step) {
        it.step = step;
        it.el.setAttribute('data-vm-step', String(step));
        it.el.dispatchEvent(new CustomEvent('vm:step', { detail: { step } }));
      }
    }
    if (it.cb) {
      try {
        it.cb(it.p, it.el);
      } catch (error) {
        console.error('[vm-motion]', error);
      }
    }
  }

  function settle(it) {
    // Final state, no tracking (reduced motion).
    it.p = 1;
    write(it);
    it.el.setAttribute('data-vm-static', '');
  }

  function trackFn(el, cb, opts = {}) {
    if (!el) return () => {};
    drop(el);
    const ds = el.dataset;
    const it = {
      el,
      cb: typeof cb === 'function' ? cb : null,
      mode: opts.mode || ds.vmScrub || 'pin',
      lerp: num(opts.lerp ?? ds.vmLerp, coarse ? 0.3 : 0.18),
      steps: Math.round(num(opts.steps ?? ds.vmSteps, 0)),
      stage: el.querySelector('[data-vm-stage]'),
      stageTop: null,
      p: 0,
      written: -1,
      step: -1,
      visible: false,
    };
    if (it.mode !== 'pin' && it.mode !== 'pass' && it.mode !== 'enter') it.mode = 'pin';
    items.set(el, it);
    if (state.reduced) {
      settle(it);
    } else {
      // Start at the true position (no lerp) so nothing flashes on load.
      it.p = progress(it, el.getBoundingClientRect(), root.clientHeight || innerHeight);
      write(it);
      el.removeAttribute('data-vm-static');
      io.observe(el);
    }
    return () => drop(el);
  }

  function drop(el) {
    const it = items.get(el);
    if (!it) return;
    io.unobserve(el);
    live.delete(it);
    items.delete(el);
    el.removeAttribute('data-vm-active');
    if (!live.size) listen(false);
  }

  /* ------------------------------------------------------------------ */
  /* In view (one-time moments)                                          */
  /* ------------------------------------------------------------------ */

  /** @type {Map<number, IntersectionObserver>} */
  const viewObservers = new Map();
  const viewFns = new WeakMap();

  function inViewFn(el, fn, opts = {}) {
    const threshold = clampFn(num(opts.threshold, 0.5), 0.01, 1);
    const once = opts.once !== false;
    let obs = viewObservers.get(threshold);
    if (!obs) {
      obs = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const rec = viewFns.get(entry.target);
            if (!rec) continue;
            if (rec.once) {
              /** @type {IntersectionObserver} */ (obs).unobserve(entry.target);
              viewFns.delete(entry.target);
            }
            rec.fn(entry.target);
          }
        },
        { threshold }
      );
      viewObservers.set(threshold, obs);
    }
    viewFns.set(el, { fn, once });
    obs.observe(el);
    const o = obs;
    return () => {
      o.unobserve(el);
      viewFns.delete(el);
    };
  }

  /* ------------------------------------------------------------------ */
  /* Autoplay containers, pause controls, lazy video                     */
  /* ------------------------------------------------------------------ */

  const autoplayObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const el = /** @type {HTMLElement} */ (entry.target);
        const vh = root.clientHeight || innerHeight;
        // Tall elements can never reach 50%: compare against the visible share.
        const share = entry.intersectionRect.height / Math.max(1, Math.min(entry.boundingClientRect.height, vh));
        if (share >= 0.5) el.removeAttribute('data-vm-offscreen');
        else if (share < 0.1) el.setAttribute('data-vm-offscreen', '');
        syncVideos(el);
      }
    },
    { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
  );

  const nearObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        nearObserver.unobserve(entry.target);
        const video = /** @type {HTMLVideoElement} */ (entry.target);
        if (!state.saveData) loadVideo(video);
      }
    },
    { rootMargin: '100% 0px' }
  );

  function bindAutoplay(el) {
    if (el.hasAttribute('data-vm-bound')) return;
    el.setAttribute('data-vm-bound', '');
    el.setAttribute('data-vm-offscreen', '');
    if (state.reduced || state.saveData) setPaused(el, true);
    else syncToggles(el);
    for (const video of el.querySelectorAll('video[data-vm-video]')) lazyVideoFn(video);
    autoplayObserver.observe(el);
  }

  function containerOf(node) {
    return node.closest('[data-vm-autoplay]');
  }

  function togglesFor(el) {
    const own = [...el.querySelectorAll('[data-vm-toggle]')];
    if (el.id) own.push(...document.querySelectorAll(`[data-vm-toggle][aria-controls="${CSS.escape(el.id)}"]`));
    return own;
  }

  function syncToggles(el) {
    const paused = el.hasAttribute('data-vm-paused');
    for (const btn of togglesFor(el)) btn.setAttribute('aria-pressed', String(paused));
  }

  function setPaused(el, paused) {
    el.toggleAttribute('data-vm-paused', paused);
    syncToggles(el);
    if (!paused) for (const video of el.querySelectorAll('video[data-vm-video]')) loadVideo(video);
    syncVideos(el);
    el.dispatchEvent(new CustomEvent('vm:autoplay', { detail: { paused } }));
  }

  function loadVideo(video) {
    if (video.hasAttribute('data-vm-loaded')) return;
    video.setAttribute('data-vm-loaded', '');
    let changed = false;
    for (const source of video.querySelectorAll('source[data-src]')) {
      source.setAttribute('src', source.getAttribute('data-src') || '');
      source.removeAttribute('data-src');
      changed = true;
    }
    if (video.dataset.src) {
      video.src = video.dataset.src;
      video.removeAttribute('data-src');
      changed = true;
    }
    if (changed) video.load();
  }

  function syncVideos(el) {
    const play = !el.hasAttribute('data-vm-paused') && !el.hasAttribute('data-vm-offscreen');
    const videos = /** @type {HTMLVideoElement[]} */ ([...el.querySelectorAll('video[data-vm-video]')]);
    for (const video of videos) {
      if (play && video.hasAttribute('data-vm-loaded')) {
        // Grouped clips (e.g. the relief split) restart from the same frame.
        if (videos.length > 1 && video !== videos[0]) video.currentTime = videos[0].currentTime;
        const attempt = video.play();
        if (attempt) attempt.catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }
    }
  }

  function lazyVideoFn(video) {
    if (!video || video.hasAttribute('data-vm-video-bound')) return;
    video.setAttribute('data-vm-video-bound', '');
    video.muted = true;
    video.playsInline = true;
    video.removeAttribute('autoplay');
    // data-vm-defer: first-viewport loops (hero) wait for the load event so
    // they never compete with the LCP still.
    if (video.hasAttribute('data-vm-defer') && document.readyState !== 'complete') {
      window.addEventListener('load', () => nearObserver.observe(video), { once: true });
    } else {
      nearObserver.observe(video);
    }
  }

  document.addEventListener('click', (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof Element)) return;

    const toggle = target.closest('[data-vm-toggle]');
    if (toggle) {
      const id = toggle.getAttribute('aria-controls');
      const el = (id && document.getElementById(id)) || containerOf(toggle);
      if (el) setPaused(el, !el.hasAttribute('data-vm-paused'));
    }
  });

  /* ------------------------------------------------------------------ */
  /* Scanning: page load, theme editor, Horizon morphs                   */
  /* ------------------------------------------------------------------ */

  function scanFn(scope = document) {
    for (const el of scope.querySelectorAll('[data-vm-scrub]')) {
      if (!items.has(el)) trackFn(el);
    }
    for (const el of scope.querySelectorAll('[data-vm-inview]:not([data-vm-seen])')) {
      if (viewFns.has(el)) continue;
      const done = (node) => {
        node.setAttribute('data-vm-seen', '');
        node.dispatchEvent(new CustomEvent('vm:inview'));
      };
      if (state.reduced) done(el);
      else inViewFn(el, done, { threshold: num(el.getAttribute('data-vm-inview'), 0.5) });
    }
    for (const el of scope.querySelectorAll('[data-vm-autoplay]')) bindAutoplay(el);
    for (const video of scope.querySelectorAll('video[data-vm-video]')) {
      if (!containerOf(video)) lazyVideoFn(video);
    }
  }

  function unscan(scope) {
    for (const el of scope.querySelectorAll('[data-vm-scrub]')) drop(el);
  }

  let pending = false;
  const mo = new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      scanFn();
    });
  });

  function start() {
    scanFn();
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  document.addEventListener('shopify:section:load', (event) => scanFn(/** @type {Element} */ (event.target)));
  document.addEventListener('shopify:section:unload', (event) => unscan(/** @type {Element} */ (event.target)));

  /* Reduced motion can change while the page is open. */
  const onReduce = (event) => {
    state.reduced = event.matches;
    root.classList.toggle('vm-rm', state.reduced);
    for (const [el, it] of [...items]) {
      if (state.reduced) {
        io.unobserve(el);
        live.delete(it);
        settle(it);
      } else {
        trackFn(el, it.cb, { mode: it.mode, lerp: it.lerp, steps: it.steps });
      }
    }
    for (const el of document.querySelectorAll('[data-vm-autoplay]')) if (state.reduced) setPaused(el, true);
    for (const fn of reducedFns) fn(state.reduced);
  };
  if (mqReduce.addEventListener) mqReduce.addEventListener('change', onReduce);

  return Object.freeze({
    version: '1.0.0',
    track: trackFn,
    untrack: drop,
    inView: inViewFn,
    lazyVideo: lazyVideoFn,
    setPaused,
    reduced: () => state.reduced,
    saveData: () => state.saveData,
    onReducedChange(fn) {
      reducedFns.add(fn);
      return () => reducedFns.delete(fn);
    },
    ramp: rampFn,
    clamp: clampFn,
    scan: scanFn,
  });
}
